import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { ThemeContext } from '../../module/ThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { IContest } from '../../types/global/helper';
import { TagsRenderer } from '../EsCards/TagsRenderer';
import { RichTextViewer } from '../Common/RichTextViewer';

interface IContestHeroProps {
    contest: IContest | null;
}

const ContestHero: React.FC<IContestHeroProps> = ({ contest }) => {
    if (!contest) return null;

    // const phaseData = getCurrentContestPhaseEndData(contest);

    // Map contest phases to human-readable labels
    const getPhaseLabel = (phase: string) => {
        switch (phase) {
            case 'preparation':
                return 'Preparation';
            case 'submission':
                return 'Submission Phase';
            case 'ideation':
                return 'Ideation Phase';
            case 'pitch':
                return 'Pitch Phase';
            case 'production':
                return 'Production Phase';
            case 'judging':
                return 'Judging Phase';
            case 'vote':
                return 'Voting Phase';
            case 'closed':
                return 'Closed';
            case 'event':
                return 'Event';
            case 'registration':
                return 'Registration';
            default:
                return phase;
        }
    };

    return (
        <ThemeContext.Consumer>
            {({ themeStyle }) => {
                return (
                    <Box
                        sx={{
                            width: '100%',
                            height: 'auto',
                            minHeight: { xs: '346px', sm: '274px' },
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            borderRadius: '20px',
                            overflow: 'hidden',
                            border: `0.2px solid ${theme[themeStyle].true_black}`,
                            mb: 4,
                        }}>
                        <Box
                            sx={{
                                backgroundImage: `url(${contest.feature_image})`,
                                backgroundColor: 'slategrey',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                backgroundRepeat: 'no-repeat',
                                height: { xs: '200px', sm: '175px' },
                                width: '100%',
                                borderTopRightRadius: '20px',
                                borderTopLeftRadius: '20px',
                                position: 'relative',
                            }}>
                            {contest.rating && (
                                <Chip
                                    label={contest.rating}
                                    size="small"
                                    sx={{
                                        position: 'absolute',
                                        top: 10,
                                        left: 10,
                                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                        color: theme[themeStyle].true_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    }}
                                />
                            )}
                            {contest.contest_phase && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        right: 20,
                                        top: 10,
                                    }}>
                                    <Chip
                                        label={getPhaseLabel(contest.contest_phase)}
                                        sx={{
                                            backgroundColor: theme[themeStyle].primary_orange,
                                            color: theme[themeStyle].pure_black,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            borderRadius: '100px',
                                            padding: '4px 8px',
                                        }}
                                    />
                                </Box>
                            )}
                        </Box>
                        <Box
                            sx={{
                                height: { xs: '150px', sm: '100px' },
                                flexDirection: 'column',
                                alignItems: { xs: 'center', sm: 'flex-start' },
                                borderBottomLeftRadius: '20px',
                                borderBottomRightRadius: '20px',
                                padding: '20px',
                                width: '100%',
                                position: 'relative',
                                backgroundColor: theme[themeStyle].deep_grey,
                                paddingLeft: 5,
                                paddingRight: 5,
                                display: 'flex',
                                justifyContent: 'center',
                            }}>
                            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                                <Typography
                                    sx={{
                                        maxWidth: { xs: '100%' },
                                        fontSize: 18,
                                        fontWeight: '700',
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        color: theme[themeStyle].filters_white,
                                    }}>
                                    {contest.title}
                                </Typography>
                                <RichTextViewer
                                    content={contest.description}
                                    characterLimit={150}
                                    enableReadMore
                                    textStyle={{ padding: 0, fontSize: 12, fontFamily: FONT_ENUM.MAIN_MEDIUM }}
                                />

                                {contest.tag && contest.tag.length > 0 && (
                                    <Box sx={{ mt: 1 }}>
                                        <TagsRenderer tags={contest.tag} maxTag={3} />
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Box>
                );
            }}
        </ThemeContext.Consumer>
    );
};

export { ContestHero };
